/**
 * Time in the household's own zone.
 *
 * Plans are stored as UTC instants but every decision a person hears is local:
 * "the Saturday after next at 7". These helpers convert between the two
 * without a date library and parse the date phrases people actually say.
 */

export const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
const WEEKDAY_NAMES: Record<Weekday, string> = {
  sun: 'Sunday',
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export type LocalDate = { y: number; m: number; d: number };
export type LocalParts = LocalDate & { hour: number; minute: number; weekday: Weekday };

export function localParts(date: Date, timeZone: string): LocalParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0';
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
    weekday: get('weekday').slice(0, 3).toLowerCase() as Weekday,
  };
}

function offsetMs(date: Date, timeZone: string): number {
  const p = localParts(date, timeZone);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.hour, p.minute);
  return asUtc - Math.floor(date.getTime() / 60_000) * 60_000;
}

/** The UTC instant of a local wall-clock time. */
export function zonedTime(day: LocalDate, hour: number, minute: number, timeZone: string): Date {
  const guess = Date.UTC(day.y, day.m - 1, day.d, hour, minute);
  const first = guess - offsetMs(new Date(guess), timeZone);
  const second = guess - offsetMs(new Date(first), timeZone);
  return new Date(second);
}

export function localDate(date: Date, timeZone: string): LocalDate {
  const { y, m, d } = localParts(date, timeZone);
  return { y, m, d };
}

export function addDays(day: LocalDate, n: number): LocalDate {
  const t = new Date(Date.UTC(day.y, day.m - 1, day.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

export function weekdayOf(day: LocalDate): Weekday {
  return WEEKDAYS[new Date(Date.UTC(day.y, day.m - 1, day.d)).getUTCDay()]!;
}

export function sameDay(a: LocalDate, b: LocalDate): boolean {
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

export function dayKey(day: LocalDate): string {
  return `${day.y}-${String(day.m).padStart(2, '0')}-${String(day.d).padStart(2, '0')}`;
}

export function weekdayName(day: Weekday): string {
  return WEEKDAY_NAMES[day];
}

function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${s}`;
}

/** "7 PM", "6:30 PM". */
export function clockLabel(date: Date, timeZone: string): string {
  const { hour, minute } = localParts(date, timeZone);
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour < 12 ? 'AM' : 'PM';
  return minute === 0 ? `${h} ${period}` : `${h}:${String(minute).padStart(2, '0')} ${period}`;
}

/** Spoken: "Saturday, October 17th at 7 PM" (time optional). */
export function speakDate(date: Date, timeZone: string, withTime = true): string {
  const p = localParts(date, timeZone);
  const base = `${WEEKDAY_NAMES[p.weekday]}, ${MONTHS[p.m - 1]} ${ordinal(p.d)}`;
  return withTime ? `${base} at ${clockLabel(date, timeZone)}` : base;
}

/** Short, relative when close: "tonight", "tomorrow", "Saturday the 17th". */
/** "today" or "tonight" (from 5 PM), "tomorrow", or "Saturday the 3rd". */
export function speakRelative(date: Date, now: Date, timeZone: string): string {
  const day = localDate(date, timeZone);
  const today = localDate(now, timeZone);
  if (sameDay(day, today)) return localParts(date, timeZone).hour >= 17 ? 'tonight' : 'today';
  if (sameDay(day, addDays(today, 1))) return 'tomorrow';
  const p = localParts(date, timeZone);
  return `${WEEKDAY_NAMES[p.weekday]} the ${ordinal(p.d)}`;
}

/** Screen: "Sat, Oct 17 · 7 PM". */
export function shortDate(date: Date, timeZone: string, withTime = true): string {
  const p = localParts(date, timeZone);
  const base = `${WEEKDAY_NAMES[p.weekday].slice(0, 3)}, ${MONTHS[p.m - 1]!.slice(0, 3)} ${p.d}`;
  return withTime ? `${base} · ${clockLabel(date, timeZone)}` : base;
}

/** Part of day a secret plan occupies, for honest deflection: "Saturday evening". */
export function partOfDay(date: Date, timeZone: string): string {
  const p = localParts(date, timeZone);
  const part = p.hour < 12 ? 'morning' : p.hour < 17 ? 'afternoon' : p.hour < 21 ? 'evening' : 'night';
  return `${WEEKDAY_NAMES[p.weekday]} ${part}`;
}

export type ParsedWhen = {
  /** Candidate days, most preferred first. */
  days: LocalDate[];
  /** How the phrase was understood, for the read-back. */
  label: string;
  /** Explicit time of day, when the phrase had one ("at 7", "7:30pm", "noon"). */
  time?: { hour: number; minute: number };
  /** Whether the phrase pinned one day (true) or a range to choose within (false). */
  exact: boolean;
  /** The phrase gave a time Rally couldn't read ("at 25"): ask rather than use the default. */
  missedTime?: boolean;
};

const WEEKDAY_WORDS: Record<string, Weekday> = {
  sunday: 'sun', sun: 'sun', monday: 'mon', mon: 'mon', tuesday: 'tue', tue: 'tue', tues: 'tue',
  wednesday: 'wed', wed: 'wed', thursday: 'thu', thu: 'thu', thurs: 'thu', friday: 'fri', fri: 'fri', saturday: 'sat', sat: 'sat',
};
const MONTH_WORDS: Record<string, number> = {
  january: 1, jan: 1, february: 2, feb: 2, march: 3, mar: 3, april: 4, apr: 4, may: 5, june: 6, jun: 6, july: 7, jul: 7,
  august: 8, aug: 8, september: 9, sep: 9, sept: 9, october: 10, oct: 10, november: 11, nov: 11, december: 12, dec: 12,
};

function nextWeekday(from: LocalDate, target: Weekday, includeToday: boolean): LocalDate {
  for (let i = includeToday ? 0 : 1; i < 8; i++) {
    const d = addDays(from, i);
    if (weekdayOf(d) === target) return d;
  }
  return from;
}

const CLOCK_WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
const utcDay = (d: LocalDate) => Date.UTC(d.y, d.m - 1, d.d);
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** "7pm", "7 p.m." and "7 PM" all read the same; commas and full stops go. */
function normalizeWhen(input: string): string {
  return input
    .toLowerCase()
    .replace(/\b([ap])\.\s?m\b\.?/g, '$1m')
    .replace(/[,.!?]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The time of day in a phrase: "7pm", "at 7", "6:30", "7 o'clock", "at seven thirty", "noon",
 * "9:30 in the morning". Numbers that belong to a date ("October 3") are never read as a time.
 * A bare "at 7" for a social plan means the evening, unless the phrase says morning.
 */
function parseTime(text: string): { hour: number; minute: number; nextDay?: boolean } | undefined {
  if (/\b(noon|midday|lunchtime|lunch)\b/.test(text)) return { hour: 12, minute: 0 };
  // Midnight starts the next day: "Saturday at midnight" is the night of Saturday into Sunday.
  if (/\bmidnight\b/.test(text)) return { hour: 0, minute: 0, nextDay: true };
  let hour: number;
  let minute: number;
  let period: string | undefined;
  const m =
    text.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/) ??
    text.match(/\bat\s+(\d{1,2})(?:(?::|\s)(\d{2}))?(?!\s*(?:st|nd|rd|th)\b)(?![\d:])/) ??
    text.match(/\b(\d{1,2}):(\d{2})\b/) ??
    text.match(/\b(\d{1,2})\s*o'?clock\b/);
  if (m) {
    hour = Number(m[1]);
    minute = Number(m[2] ?? 0);
    period = m[3];
  } else {
    const w = text.match(/\bat\s+(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)(?:[\s-](o'?clock|fifteen|thirty|forty[\s-]five))?(?:\s+(am|pm))?\b/);
    if (w) {
      hour = CLOCK_WORDS[w[1]!]!;
      minute = !w[2] ? 0 : w[2].startsWith('fifteen') ? 15 : w[2].startsWith('thirty') ? 30 : w[2].startsWith('forty') ? 45 : 0;
      period = w[3];
    } else if (/\bmorning\b/.test(text)) return { hour: 10, minute: 0 };
    else if (/\bafternoon\b/.test(text)) return { hour: 14, minute: 0 };
    else return undefined;
  }
  if (hour > 23 || minute > 59) return undefined;
  if (period === 'pm' && hour < 12) hour += 12;
  if ((period === 'am' || (!period && /\bmorning\b/.test(text))) && hour === 12) hour = 0;
  if (!period && hour >= 1 && hour <= 11) {
    // "at 7" for a social plan means evening; "9:30 in the morning" doesn't, and "tonight at 11" is late.
    const morning = /\bmorning\b/.test(text);
    const evening = /\b(tonight|evening|night|afternoon)\b/.test(text);
    if (evening || (!morning && hour <= 10)) hour += 12;
  }
  return { hour, minute };
}

/** Something in the phrase is a time of day, read or not ("at 25" is still a time). */
const SAYS_TIME = /\b\d{1,2}(?::\d{2})?\s*(?:am|pm)\b|\bat\s+(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b|\b\d{1,2}:\d{2}\b|o'?clock\b|\b(?:noon|midday|midnight)\b/;

/** Evening days that suit a plan within a range, weekend-leaning. */
function rangeDays(start: LocalDate, count: number): LocalDate[] {
  const order: Record<Weekday, number> = { sat: 0, fri: 1, thu: 2, sun: 3, wed: 4, tue: 5, mon: 6 };
  return Array.from({ length: count }, (_, i) => addDays(start, i)).sort((a, b) => order[weekdayOf(a)] - order[weekdayOf(b)]);
}

/** The nth weekday of a month: Thanksgiving is the fourth Thursday of November. */
function nthWeekday(y: number, m: number, target: Weekday, n: number): LocalDate {
  let d: LocalDate = { y, m, d: 1 };
  while (weekdayOf(d) !== target) d = addDays(d, 1);
  return addDays(d, 7 * (n - 1));
}

/** Holidays people plan around, each on its next date (today counts). */
const HOLIDAYS: Array<[RegExp, string, (y: number) => LocalDate]> = [
  [/\bhalloween\b/, 'Halloween', (y) => ({ y, m: 10, d: 31 })],
  [/\bchristmas eve\b/, 'Christmas Eve', (y) => ({ y, m: 12, d: 24 })],
  [/\b(?:christmas|xmas)(?: day)?\b/, 'Christmas Day', (y) => ({ y, m: 12, d: 25 })],
  [/\bnew years?'?s? eve\b/, "New Year's Eve", (y) => ({ y, m: 12, d: 31 })],
  [/\bnew years?'?s? day\b/, "New Year's Day", (y) => ({ y, m: 1, d: 1 })],
  [/\bvalentine'?s?(?: day)?\b/, "Valentine's Day", (y) => ({ y, m: 2, d: 14 })],
  [/\b(?:st|saint) patrick'?s(?: day)?\b/, "St. Patrick's Day", (y) => ({ y, m: 3, d: 17 })],
  [/\bcinco de mayo\b/, 'Cinco de Mayo', (y) => ({ y, m: 5, d: 5 })],
  [/\bfourth of july\b|\bindependence day\b/, 'the Fourth of July', (y) => ({ y, m: 7, d: 4 })],
  [/\bthanksgiving\b/, 'Thanksgiving', (y) => nthWeekday(y, 11, 'thu', 4)],
];

type Days = Omit<ParsedWhen, 'time' | 'missedTime'>;

/** The day or days a phrase names, leaving its time of day to parseTime. */
function parseDays(text: string, today: LocalDate, timeZone: string): Days | undefined {
  // "A week from Saturday", "two weeks from tomorrow": the day named, then whole weeks on.
  const weeksFrom = text.match(/\b(a|one|two|three|1|2|3)\s+weeks?\s+from\s+((?:this\s+|next\s+)?(?:sunday|monday|tuesday|wednesday|thursday|friday|saturday)|today|tonight|tomorrow|now)\b/);
  if (weeksFrom) {
    const base = /^(today|tonight|now)$/.test(weeksFrom[2]!) ? today : parseDays(weeksFrom[2]!, today, timeZone)?.days[0];
    const n = ({ a: 1, one: 1, '1': 1, two: 2, '2': 2, three: 3, '3': 3 } as Record<string, number>)[weeksFrom[1]!]!;
    if (base) return { days: [addDays(base, 7 * n)], label: weeksFrom[0], exact: true };
  }

  for (const [re, label, on] of HOLIDAYS) {
    if (!re.test(text)) continue;
    const d = [today.y, today.y + 1].map(on).find((x) => utcDay(x) >= utcDay(today))!;
    return { days: [d], label, exact: true };
  }

  if (/\btonight\b/.test(text)) return { days: [today], label: 'tonight', exact: true };
  if (/\btomorrow\b/.test(text)) return { days: [addDays(today, 1)], label: 'tomorrow', exact: true };

  const afterNext = text.match(/\b(\w+day|sat|sun|fri)\s+after\s+next\b/);
  if (afterNext && WEEKDAY_WORDS[afterNext[1]!]) {
    const d = addDays(nextWeekday(today, WEEKDAY_WORDS[afterNext[1]!]!, false), 7);
    return { days: [d], label: `the ${weekdayName(weekdayOf(d))} after next`, exact: true };
  }

  // "October 17th", "Oct 17", "17 October". "October 3 7pm": the 7 is a time, the 3 the day.
  const monthFirst = text.match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2})(?:st|nd|rd|th)?\b(?!\s*(?::\d|am\b|pm\b|o'?clock))/);
  const dayFirst = text.match(/\b(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/);
  const md = monthFirst ? { m: MONTH_WORDS[monthFirst[1]!]!, d: Number(monthFirst[2]) } : dayFirst ? { m: MONTH_WORDS[dayFirst[2]!]!, d: Number(dayFirst[1]) } : undefined;
  if (md && md.d >= 1 && md.d <= 31) {
    let y = today.y;
    if (md.m < today.m || (md.m === today.m && md.d < today.d)) y += 1;
    const d = { y, m: md.m, d: md.d };
    return { days: [d], label: speakDate(zonedTime(d, 12, 0, timeZone), timeZone, false), exact: true };
  }
  // "the 17th"
  const theNth = text.match(/\bthe\s+(\d{1,2})(?:st|nd|rd|th)\b/);
  if (theNth) {
    const n = Number(theNth[1]);
    let d = { y: today.y, m: today.m, d: n };
    if (n < today.d) d = today.m === 12 ? { y: today.y + 1, m: 1, d: n } : { y: today.y, m: today.m + 1, d: n };
    return { days: [d], label: speakDate(zonedTime(d, 12, 0, timeZone), timeZone, false), exact: true };
  }

  // A month without a day: "sometime next month", "later this month", "in November". A weekday named narrows it.
  const weekday = text.match(/\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday)s?\b/)?.[1];
  const inMonth = (y: number, m: number, from: number, label: string): Days | undefined => {
    const last = daysInMonth(y, m);
    const first = Math.max(from, /\blate\b|\bend of\b/.test(text) ? last - 9 : 1);
    const end = /\bearly\b|\bbeginning of\b|\bstart of\b/.test(text) ? Math.min(last, 10) : last;
    if (first > end) return undefined;
    const days = rangeDays({ y, m, d: first }, end - first + 1).filter((d) => !weekday || weekdayOf(d) === WEEKDAY_WORDS[weekday]);
    return days.length ? { days, label, exact: false } : undefined;
  };
  const nextMonth = /\bnext\s+month\b/.test(text) && inMonth(today.m === 12 ? today.y + 1 : today.y, today.m === 12 ? 1 : today.m + 1, 1, 'next month');
  if (nextMonth) return nextMonth;
  const thisMonth = /\bthis\s+month\b/.test(text) && inMonth(today.y, today.m, today.d + 1, 'later this month');
  if (thisMonth) return thisMonth;
  const monthName = text.match(/\b(?:in|of|during)\s+(may)\b|\b(january|february|march|april|june|july|august|september|october|november|december)\b/);
  if (monthName) {
    const m = MONTH_WORDS[(monthName[1] ?? monthName[2])!]!;
    const y = m < today.m ? today.y + 1 : today.y;
    const found = inMonth(y, m, m === today.m && y === today.y ? today.d + 1 : 1, `in ${MONTHS[m - 1]}`);
    if (found) return found;
  }

  const nextNamed = text.match(/\bnext\s+(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/);
  if (nextNamed) {
    // "next Saturday" = the Saturday of next week (never the day after tomorrow).
    const target = WEEKDAY_WORDS[nextNamed[1]!]!;
    let d = nextWeekday(today, target, false);
    const daysUntilSunday = (7 - new Date(utcDay(today)).getUTCDay()) % 7;
    const dist = Math.round((utcDay(d) - utcDay(today)) / 86_400_000);
    if (dist <= daysUntilSunday) d = addDays(d, 7);
    return { days: [d], label: `next ${weekdayName(target)}`, exact: true };
  }
  const named = text.match(/\b(?:this\s+|on\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/);
  if (named) {
    const target = WEEKDAY_WORDS[named[1]!]!;
    const d = nextWeekday(today, target, true);
    return { days: [d], label: weekdayName(target), exact: true };
  }

  const toSunday = (7 - new Date(utcDay(today)).getUTCDay()) % 7;
  if (/\bnext\s+weekend\b/.test(text)) {
    const fri = addDays(today, toSunday + 5);
    return { days: [addDays(fri, 1), fri, addDays(fri, 2)], label: 'next weekend', exact: false };
  }
  if (/\b(this\s+)?weekend\b/.test(text)) {
    const sat = nextWeekday(today, 'sat', true);
    return { days: [sat, addDays(sat, -1), addDays(sat, 1)].filter((d) => utcDay(d) >= utcDay(today)), label: 'this weekend', exact: false };
  }
  if (/\bin\s+(two|2)\s+weeks\b|\bweek\s+after\s+next\b/.test(text)) {
    return { days: rangeDays(addDays(today, toSunday + 8), 7), label: 'the week after next', exact: false };
  }
  if (/\bnext\s+week\b/.test(text)) {
    return { days: rangeDays(addDays(today, toSunday + 1), 7), label: 'next week', exact: false };
  }
  if (/\bthis\s+week\b/.test(text)) {
    return { days: rangeDays(addDays(today, 1), Math.max(1, toSunday)), label: 'this week', exact: false };
  }
  if (/\bsoon\b|\bsometime\b/.test(text)) {
    return { days: rangeDays(addDays(today, 2), 12), label: 'in the next two weeks', exact: false };
  }
  return undefined;
}

/**
 * Parse what people say about when: "tonight", "tomorrow at noon", "Saturday",
 * "this weekend", "next week", "the Saturday after next", "a week from Saturday",
 * "October 17th at 7pm", "the 17th", "in two weeks", "Halloween", "sometime next month".
 * Returns undefined when nothing date-like is found; a time on its own ("8 pm") is parseClock's.
 */
export function parseWhen(input: string, now: Date, timeZone: string): ParsedWhen | undefined {
  const text = normalizeWhen(input);
  const found = parseDays(text, localDate(now, timeZone), timeZone);
  if (!found) return undefined;
  const clock = parseTime(text);
  // Midnight belongs to the next day's date.
  const days = clock?.nextDay ? found.days.map((d) => addDays(d, 1)) : found.days;
  return {
    ...found,
    days,
    ...(clock ? { time: { hour: clock.hour, minute: clock.minute } } : {}),
    ...(!clock && SAYS_TIME.test(text) ? { missedTime: true } : {}),
  };
}

/**
 * A time of day said on its own, with no day: "8 pm", "at 8", "8:30", "noon". Undefined for a
 * phrase that names a day too, or has other words in it ("Lark at 8").
 */
export function parseClock(input: string): { hour: number; minute: number } | undefined {
  const text = normalizeWhen(input);
  const clock = parseTime(text);
  if (!clock || clock.nextDay || parseDays(text, { y: 2000, m: 1, d: 1 }, 'UTC')) return undefined;
  const rest = text
    .replace(/\d{1,2}(:\d{2})?/g, ' ')
    .replace(/\b(at|around|about|by|make it|to|for|instead|say|am|pm|o'?clock|noon|midday|lunchtime|lunch|in|the|morning|evening|night|afternoon|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|fifteen|thirty|forty)\b/g, ' ')
    .replace(/[-\s]+/g, '');
  return rest ? undefined : { hour: clock.hour, minute: clock.minute };
}
